import { ReactNode, createContext, useContext } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { wacToken } from "@/libraries/design/foundations";

// Create a context to track if we're inside TypographyStyles
const TypographyStylesContext = createContext<boolean>(false);

// FY24 rebrand type matrix rules as per https://www.figma.com/file/RZRs55BkCWtX6zBPUD7dRb/Type-Matrix-React---FY24-Updates?type=design&node-id=323-741&mode=design&t=7RUZPtxLZpLvX1Q5-0
// colors
const PRIMARY_BLACK = "#101214";
const NEUTRAL_LIGHT = "#292A2E";

const typographyStyles = css({
    // headlines
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h1, .h1": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 800,
        color: `${PRIMARY_BLACK}`,
        fontSize: "2.5rem",
        lineHeight: "2.875rem",

        [media.only.sm]: {
            fontSize: "2.75rem",
            lineHeight: "3.125rem"
        },
        [media.above.md]: {
            fontSize: "3rem",
            lineHeight: "3.5rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, .h2": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "2rem",
        lineHeight: "2.375rem",

        [media.only.sm]: {
            fontSize: "2.25rem",
            lineHeight: "2.625rem"
        },
        [media.above.md]: {
            fontSize: "2.5rem",
            lineHeight: "3rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h3, .h3": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "1.5rem",
        lineHeight: "1.875rem",

        [media.only.sm]: {
            fontSize: "1.75rem",
            lineHeight: "2.125rem"
        },
        [media.above.md]: {
            fontSize: "2rem",
            lineHeight: "2.375rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h4, .h4": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "1.25rem",
        lineHeight: "1.625rem",

        [media.only.sm]: {
            fontSize: "1.375rem",
            lineHeight: "1.75rem"
        },
        [media.above.md]: {
            fontSize: "1.5rem",
            lineHeight: "1.875rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h5, .h5": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
        color: `${PRIMARY_BLACK}`,

        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.75rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h6, .h6": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        color: `${PRIMARY_BLACK}`
    },

    // body
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".body-lg": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.75rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p, .body-md": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        color: `${NEUTRAL_LIGHT}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".body-sm": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.25rem",
        color: `${NEUTRAL_LIGHT}`
    },

    // additional styles
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".superhero, .superhero-contrast": {
        fontFamily: wacToken("font.charlie.display"),
        fontSize: "3.125rem",
        lineHeight: "3.75rem",
        color: `${PRIMARY_BLACK}`,

        [media.only.sm]: {
            fontSize: "3.75rem",
            lineHeight: "4.375rem"
        },
        [media.above.md]: {
            fontSize: "4.375rem",
            lineHeight: "5rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".superhero": {
        fontWeight: 800
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".superhero-contrast": {
        fontWeight: 400
    },

    // card titles
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".card-title": {
        fontFamily: wacToken("font.charlie.display"),
        fontSize: "1.25rem",
        fontWeight: 700,
        lineHeight: "1.625rem",
        color: `${PRIMARY_BLACK}`,
        letterSpacing: "0.3px",

        [media.only.sm]: {
            fontSize: "1.375rem",
            lineHeight: "1.75rem"
        },
        [media.above.md]: {
            fontSize: "1.5rem",
            lineHeight: "1.875rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".card-title-sm": {
        fontFamily: wacToken("font.charlie.display"),
        fontSize: "1.125rem",
        fontWeight: 700,
        lineHeight: "1.5rem",
        color: `${PRIMARY_BLACK}`,
        letterSpacing: "0.3px",

        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.625rem"
        }
    },

    // captions
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".fine-print": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1.125rem",
        color: `${NEUTRAL_LIGHT}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".fine-print-sm": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "0.625rem",
        lineHeight: "1rem",
        color: `${NEUTRAL_LIGHT}`
    },

    // links
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link": {
        fontFamily: wacToken("font.charlie.text"),
        fontSize: "1rem",
        lineHeight: "1.5rem",
        fontWeight: 600,
        color: `${NEUTRAL_LIGHT}`
    },

    // eyebrow
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".eyebrow-lg": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        textTransform: "uppercase",
        color: `${NEUTRAL_LIGHT}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".eyebrow-md": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.125rem",
        textTransform: "uppercase",
        color: `${NEUTRAL_LIGHT}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".eyebrow-sm": {
        fontFamily: wacToken("font.charlie.text"),
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1rem",
        textTransform: "uppercase",
        color: `${NEUTRAL_LIGHT}`
    },

    // callouts
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".callout": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 300,
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.only.sm]: {
            fontSize: "1.5rem",
            lineHeight: "2rem"
        },
        [media.above.md]: {
            fontWeight: 400,
            fontSize: "1.75rem",
            lineHeight: "2.5rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".callout-lg": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 300,
        fontSize: "1.75rem",
        lineHeight: "2.5rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.only.sm]: {
            fontSize: "2rem",
            lineHeight: "2.75rem"
        },
        [media.above.md]: {
            fontWeight: 400,
            fontSize: "2.25rem",
            lineHeight: "3rem"
        }
    },

    // stats
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".stat-lg": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 800,
        color: wacToken("color.primary.blue"),
        fontSize: "4.375rem",
        lineHeight: "5rem",

        [media.only.sm]: {
            fontSize: "3.75rem",
            lineHeight: "4.375rem"
        },
        [media.above.md]: {
            fontSize: "3.125rem",
            lineHeight: "3.75rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".stat-sm": {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 800,
        color: wacToken("color.primary.blue"),
        fontSize: "2.5rem",
        lineHeight: "2.875rem",

        [media.only.sm]: {
            fontSize: "2.75rem",
            lineHeight: "3.125rem"
        },
        [media.above.md]: {
            fontSize: "3rem",
            lineHeight: "3.5rem"
        }
    }
});

interface TypographyStylesInterface {
    children: ReactNode;
}

/**
 * @description wrapper component used to add global font styles
 * If already inside another TypographyStyles component, it will skip re-rendering the styles
 */
export const TypographyStyles = ({ children }: TypographyStylesInterface) => {
    const isInsideTypographyStyles = useContext(TypographyStylesContext);

    // If we're already inside TypographyStyles, just render children without the styles
    if (isInsideTypographyStyles) {
        return <div>{children}</div>;
    }

    return (
        <TypographyStylesContext.Provider value={true}>
            <div css={typographyStyles}>{children}</div>
        </TypographyStylesContext.Provider>
    );
};
